import * as React from 'react';
import { RootState } from '../reducers/reducers';
import { useDispatch, useSelector } from 'react-redux';
import {
    getIsLoading,
    openLinkInNewTab,
    getUpdated,
    getError,
    screenshotSpecificationsURLForPlatform,
    shouldDisableMetadataScreenshotsEditing,
} from '../helpers';
import styles from './styles/stepStyles';
import {
    AppPlatform,
    AppPreviewLocalization,
    ReleaseStepMetadataData,
    ReleaseStepScreenshotsData,
    ReleaseStepStatus,
    ReleaseStepType,
    RunwayResponseType,
    ScreenshotLocalization,
    ScreenshotSet,
} from '../types';
import { UpdateScreenshotsApprovalRequestReleaseStep, tryUpdateRelease } from '../actions/release';
import { ReleaseUpdatable } from '../actions/updatable';
import { FeatureSection } from '../typescriptHelpers';
import { ReleaseStepInactiveContext } from '../contexts';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import SmallStatusNode from './SmallStatusNode';
import { displayConsoleNameWithPrefix, statusColorForLocalizationStatus } from '../helpers/displayHelpers';
import { LocalizationStatus } from '../types';
import Tabs from 'antd/lib/tabs';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import { CheckOutlined, ArrowRightOutlined, CloseOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import {
    selectRelease,
    selectAppStoreType,
    selectAppPlatform,
    selectReleaseStep,
    selectStepRun,
} from '../selectors/selectors';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';
import { globalMessage } from '../utils/globalMessage';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import { iconForPlatform } from '../helpers/platformComponentHelpers';
import { StepContentVideoAndScreenshotsLocalizedFrom } from '@/components/StepContentVideoAndScreenshotsLocalizedFrom';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { StepRunUpdatable } from '@/actions/stepRun';
import { useCallUpdatable } from '@/utils/hooks/helpers';

export const StepContentScreenshots = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    stepRunComponentProps,
}: ReleaseStepOrStepRunComponentProps) => {
    const dispatch = useDispatch();
    const isStepInactive = React.useContext(ReleaseStepInactiveContext);

    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const releaseStep = useSelector((state: RootState) => selectReleaseStep(state, releaseId, releaseStepType));
    const stepRunId = stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const stepData: ReleaseStepScreenshotsData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;

    const updateScreenshotsApprovalIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.ScreenshotsApproval, state, releaseId),
    );

    const screenshotsApprovalUpdatedReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.ScreenshotsApproval, state, releaseId),
    );
    const screenshotsApprovalErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.ScreenshotsApproval, state, releaseId),
    );

    const [
        updateScreenshotsApprovalStepRun,
        updateScreenshotsApprovalIsLoadingStepRun,
        updateScreenshotsApprovalResponseTypeStepRun,
        updateScreenshotsApprovalErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.UpdateScreenshotsApproval, stepRunComponentProps);

    const updateScreenshotsApprovalIsLoading =
        stepRun ? updateScreenshotsApprovalIsLoadingStepRun : updateScreenshotsApprovalIsLoadingReleaseStep;

    const screenshotsApprovalUpdated =
        stepRun ?
            updateScreenshotsApprovalResponseTypeStepRun === RunwayResponseType.Success
        :   screenshotsApprovalUpdatedReleaseStep;

    const screenshotsApprovalError =
        stepRun ? updateScreenshotsApprovalErrorStepRun : screenshotsApprovalErrorReleaseStep;

    const userId = useSelector((state: RootState) => state.user.id);
    const isApproved = stepData.isApproved;
    const platform = useSelector((state: RootState) => {
        return stepRun ? stepData.platform : selectAppPlatform(state, appId);
    });
    const appStoreType = useSelector((state: RootState) => {
        return stepRun ? stepData.appStoreType : selectAppStoreType(state, appId);
    });
    const stepStatus = stepRun ? stepRun.status : releaseStep.status;
    const disableApproveButton = stepStatus === ReleaseStepStatus.Blocked;
    const primaryLocale = stepData?.primaryLocale;

    const metadataReleaseStepData = useSelector((state: RootState) =>
        selectReleaseStep(state, releaseId, ReleaseStepType.Metadata),
    )?.data as ReleaseStepMetadataData;

    const androidVideoUrlLocalizations = metadataReleaseStepData?.localizations ?? [];

    const appPreviewLocalizationsMap =
        stepData.appPreviewLocalizations?.reduce((acc, localization) => {
            acc[localization.locale] = localization;
            return acc;
        }, {}) ?? {};

    const validatedLocalizations =
        stepData.screenshotLocalizations
            ?.map((localizedScreenshotsForLocale) => {
                const hasMinimumScreenshots = hasMinimumScreenshotsAtLeast(localizedScreenshotsForLocale, platform);
                const hasErroredScreenshotSet = localizedScreenshotsForLocale.screenshotSets.some((screenshotSet) =>
                    screenshotSet.screenshots.some((screenshot) => !!screenshot.uploadError),
                );

                const appPreviewsLocalization = appPreviewLocalizationsMap[
                    localizedScreenshotsForLocale.locale
                ] as AppPreviewLocalization;

                const hasErroredAppPreview = appPreviewsLocalization?.appPreviewSets?.some((appPreviewSet) =>
                    appPreviewSet.appPreviews?.some((appPreview) => !!appPreview.uploadError),
                );

                const status =
                    hasErroredScreenshotSet || hasErroredAppPreview ? LocalizationStatus.Blocked
                    : hasMinimumScreenshots ? LocalizationStatus.Ready
                    : LocalizationStatus.Pending;

                return {
                    ...localizedScreenshotsForLocale,
                    appPreviewSets: appPreviewsLocalization?.appPreviewSets ?? [],
                    status,
                };
            })
            .sort((a, b) => {
                if (a.locale === primaryLocale) {
                    return -1;
                } else if (b.locale === primaryLocale) {
                    return 1;
                }
                return a.locale.localeCompare(b.locale);
            }) ?? [];

    const screenshotsSpecificationUrl = screenshotSpecificationsURLForPlatform(platform);
    const appStoreUrl = stepData.storeUrl;

    const disableScreenshotsEditing = shouldDisableMetadataScreenshotsEditing(
        appStoreType,
        release.status,
        release.type,
    );

    const updateScreenshotsApproval = (isApproved: boolean) => {
        if (stepRun) {
            return updateScreenshotsApprovalStepRun({ isApproved });
        }

        const request: UpdateScreenshotsApprovalRequestReleaseStep = {
            orgId,
            appId,
            userId,
            releaseId,
            isApproved,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.ScreenshotsApproval, request, request.releaseId));
    };

    React.useEffect(() => {
        if (screenshotsApprovalUpdated) {
            globalMessage.success('Screenshots approval updated!');
        }
    }, [screenshotsApprovalUpdated]);

    React.useEffect(() => {
        if (screenshotsApprovalError) {
            globalMessage.error(screenshotsApprovalError);
        }
    }, [screenshotsApprovalError]);

    return (
        <Layout style={{ height: '100%' }}>
            <Content
                style={{
                    ...styles.innerContent,
                    ...styles.spaceBetween,
                    ...styles.contentWithFooterStyle,
                    paddingTop: 0,
                }}
            >
                <div style={{ width: '100%', flexGrow: 1 }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 28 }}>
                            <Text style={{ color: antGrey.grey7, flex: 1 }}>
                                View and edit screenshots for this release
                            </Text>
                            <Space>
                                <Button
                                    type="link"
                                    icon={iconForPlatform(platform)}
                                    target="_blank"
                                    href={screenshotsSpecificationUrl}
                                    style={{ padding: 0 }}
                                >
                                    Screenshot specifications
                                </Button>
                            </Space>
                        </div>
                        <ReleaseEventsTimelineSection
                            orgId={orgId}
                            appId={appId}
                            releaseId={releaseId}
                            stepRunId={stepRunComponentProps?.stepRunId}
                            context={ReleaseTimelineSectionContext.ReleaseStep}
                            maxItemsToDisplay={1}
                            releaseStepType={releaseStepType}
                        />
                    </div>

                    <Tabs destroyInactiveTabPane={true} style={{ height: '100%' }}>
                        {validatedLocalizations?.map((localization, index) => {
                            let displayLocale = localization.displayLocale;
                            if (localization.locale === primaryLocale) {
                                displayLocale = `${displayLocale} (Primary)`;
                            }

                            return (
                                <Tabs.TabPane
                                    key={`tab-${index}`}
                                    tab={
                                        <Space>
                                            {displayLocale}{' '}
                                            <SmallStatusNode
                                                color={statusColorForLocalizationStatus(localization.status)}
                                            />
                                        </Space>
                                    }
                                    style={styles.screenshotsLocalizationContentWrapper}
                                >
                                    <EmptyStateWrapper
                                        releaseStepType={releaseStepType}
                                        appId={appId}
                                        featureSections={[FeatureSection.ScreenshotsTabsContent]}
                                        releaseId={releaseId}
                                        shouldCenterContent
                                    >
                                        <div>
                                            <StepContentVideoAndScreenshotsLocalizedFrom
                                                stepRunComponentProps={stepRunComponentProps}
                                                localization={localization}
                                                platform={platform}
                                                appId={appId}
                                                orgId={orgId}
                                                releaseId={releaseId}
                                                disableEditing={disableScreenshotsEditing}
                                                androidVideoUrlLocalizations={androidVideoUrlLocalizations}
                                            />
                                        </div>
                                    </EmptyStateWrapper>
                                </Tabs.TabPane>
                            );
                        })}
                        {(!validatedLocalizations || validatedLocalizations?.length === 0) && (
                            <EmptyStateWrapper
                                releaseStepType={releaseStepType}
                                appId={appId}
                                featureSections={[FeatureSection.ScreenshotsTabsContent]}
                                releaseId={releaseId}
                                shouldCenterContent
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexGrow: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Text style={{ fontSize: 14 }} type="secondary">
                                        No screenshots found.
                                    </Text>
                                </div>
                            </EmptyStateWrapper>
                        )}
                    </Tabs>
                </div>
            </Content>

            <Footer style={styles.footerStyle}>
                <Button
                    style={{ marginRight: 20 }}
                    disabled={isStepInactive}
                    onClick={() => openLinkInNewTab(appStoreUrl)}
                    icon={<ArrowRightOutlined style={{ color: runwayColors.primary }} />}
                >
                    View screenshots in {displayConsoleNameWithPrefix(appStoreType)}
                </Button>
                <PermissionedActionButton
                    releaseId={releaseId}
                    actionType={RunwayUserAction.UAApproveScreenshotsMetadata}
                    loading={updateScreenshotsApprovalIsLoading}
                    disabled={disableApproveButton}
                    onClick={() => updateScreenshotsApproval(!isApproved)}
                    type={isApproved ? 'default' : 'primary'}
                    icon={isApproved ? <CloseOutlined style={{ color: runwayColors.statusRed }} /> : <CheckOutlined />}
                >
                    {isApproved ? `Rescind screenshots approval` : `Approve screenshots`}
                </PermissionedActionButton>
            </Footer>
        </Layout>
    );
};

const hasRequiredScreenshotsForPlatform = (platform: AppPlatform, screenshotSets: ScreenshotSet[]) => {
    const screenshotTypes = screenshotSets.map((set) => set.screenshotDisplayType);
    switch (platform) {
        case AppPlatform.Ios: {
            const required = ['APP_IPHONE_67', 'APP_IPHONE_65'];
            for (const requiredType in required) {
                // iOS requires one of 6.9" or 6.5"
                if (screenshotTypes.includes(requiredType)) {
                    return true;
                }
            }
            return false;
        }
        case AppPlatform.TvOS:
            return screenshotTypes.includes('APP_APPLE_TV');
        case AppPlatform.VisionOS:
            return screenshotTypes.includes('APP_APPLE_VISION_PRO');
        default:
            return true;
    }
};

const hasMinimumScreenshotsAtLeast = (localization: ScreenshotLocalization, platform: AppPlatform): boolean => {
    return hasRequiredScreenshotsForPlatform(platform, localization.screenshotSets);
};
